import { FormSelectOption } from '@oneview/controls';
import { TradingFormTranslationKey } from './TradingFormContext';
import {
    AlgoStrategy,
    OptionStrategy,
    OrderFillType,
    OrderMarketTime,
    OrderTransaction,
    OrderType,
    OrderValidity,
    PositionType,
    PriceType,
    TradeDestination,
    TrailingStopType,
} from './lookups';
import { SecurityType } from '@orbis-systems/orbis-api-middleware';

export type TradingFormSelectOption = Omit<FormSelectOption, 'name'> & {
    name: TradingFormTranslationKey;
};

export const TRADE_DESTINATION_OPTIONS: {
    singleAccount: TradingFormSelectOption;
    averagePriceAccount: TradingFormSelectOption;
    model: TradingFormSelectOption;
} = {
    singleAccount: {
        name: 'tradeDestinationSelect.options.singleAccount',
        value: TradeDestination.SingleAccount,
    },
    averagePriceAccount: {
        name: 'tradeDestinationSelect.options.averagePriceAccount',
        value: TradeDestination.AveragePriceAccount,
    },
    model: {
        name: 'tradeDestinationSelect.options.model',
        value: TradeDestination.Model,
    },
};

export const SECURITY_TYPE_OPTIONS: {
    equity: TradingFormSelectOption;
    option: TradingFormSelectOption;
    mutualFund: TradingFormSelectOption;
    bond: TradingFormSelectOption;
} = {
    equity: {
        name: 'securityTypeSelect.options.equity',
        value: SecurityType.Equity,
    },
    option: {
        name: 'securityTypeSelect.options.option',
        value: SecurityType.Option,
    },
    mutualFund: {
        name: 'securityTypeSelect.options.mutualFund',
        value: SecurityType.MutualFund,
    },
    bond: {
        name: 'securityTypeSelect.options.bond',
        value: SecurityType.Bond,
    },
};

export const TRANSACTION_OPTIONS: {
    buy: TradingFormSelectOption;
    sell: TradingFormSelectOption;
    shortSell: TradingFormSelectOption;
    buyToCover: TradingFormSelectOption;
} = {
    buy: {
        name: 'transactionSelect.options.buy',
        value: OrderTransaction.Buy,
    },
    sell: {
        name: 'transactionSelect.options.sell',
        value: OrderTransaction.Sell,
    },
    shortSell: {
        name: 'transactionSelect.options.shortSell',
        value: OrderTransaction.ShortSell,
    },
    buyToCover: {
        name: 'transactionSelect.options.buyToCover',
        value: OrderTransaction.BuyToCover,
    },
};

export const MUTUAL_FUND_TRANSACTION_OPTIONS: {
    buy: TradingFormSelectOption;
    sell: TradingFormSelectOption;
} = {
    buy: {
        name: 'transactionSelect.options.buy',
        value: OrderTransaction.Buy,
    },
    sell: {
        name: 'transactionSelect.options.redeem',
        value: OrderTransaction.Sell,
    },
};

export const OPTION_TRANSACTION_OPTIONS: {
    buyToOpen: TradingFormSelectOption;
    sellToClose: TradingFormSelectOption;
    sellToOpen: TradingFormSelectOption;
    buyToClose: TradingFormSelectOption;
} = {
    buyToOpen: {
        name: 'transactionSelect.options.buyToOpen',
        value: OrderTransaction.BuyToOpen,
    },
    sellToClose: {
        name: 'transactionSelect.options.sellToClose',
        value: OrderTransaction.SellToClose,
    },
    sellToOpen: {
        name: 'transactionSelect.options.sellToOpen',
        value: OrderTransaction.SellToOpen,
    },
    buyToClose: {
        name: 'transactionSelect.options.buyToClose',
        value: OrderTransaction.BuyToClose,
    },
};

export const SPREAD_TRANSACTION_OPTIONS: {
    open: TradingFormSelectOption;
    close: TradingFormSelectOption;
} = {
    open: {
        name: 'transactionSelect.options.open',
        value: OrderTransaction.Open,
    },
    close: {
        name: 'transactionSelect.options.close',
        value: OrderTransaction.Close,
    },
};

export const ORDER_TYPE_OPTIONS: {
    market: TradingFormSelectOption;
    limit: TradingFormSelectOption;
    stop: TradingFormSelectOption;
    stopLimit: TradingFormSelectOption;
    notional: TradingFormSelectOption;
    trailingStop: TradingFormSelectOption;
    marketOnClose: TradingFormSelectOption;
} = {
    market: {
        name: 'orderTypeSelect.options.market',
        value: OrderType.Market,
    },
    limit: {
        name: 'orderTypeSelect.options.limit',
        value: OrderType.Limit,
    },
    stop: {
        name: 'orderTypeSelect.options.stop',
        value: OrderType.Stop,
    },
    stopLimit: {
        name: 'orderTypeSelect.options.stopLimit',
        value: OrderType.StopLimit,
    },
    notional: {
        name: 'orderTypeSelect.options.notional',
        value: OrderType.Notional,
    },
    trailingStop: {
        name: 'orderTypeSelect.options.trailingStop',
        value: OrderType.TrailingStop,
    },
    marketOnClose: {
        name: 'orderTypeSelect.options.marketOnClose',
        value: OrderType.MarketOnClose,
    },
};

export const SPREAD_ORDER_TYPE_OPTION: {
    market: TradingFormSelectOption;
    even: TradingFormSelectOption;
    debit: TradingFormSelectOption;
    credit: TradingFormSelectOption;
} = {
    market: {
        name: 'orderTypeSelect.options.market',
        value: OrderType.Market,
    },
    even: {
        name: 'orderTypeSelect.options.even',
        value: OrderType.Even,
    },
    debit: {
        name: 'orderTypeSelect.options.debit',
        value: OrderType.Debit,
    },
    credit: {
        name: 'orderTypeSelect.options.credit',
        value: OrderType.Credit,
    },
};

export const ALGO_STRATEGY_OPTIONS: {
    vwap: TradingFormSelectOption;
    twap: TradingFormSelectOption;
    pov: TradingFormSelectOption;
} = {
    vwap: {
        name: 'algoStrategySelect.options.vwap',
        value: AlgoStrategy.VWAP,
    },
    twap: {
        name: 'algoStrategySelect.options.twap',
        value: AlgoStrategy.TWAP,
    },
    pov: {
        name: 'algoStrategySelect.options.pov',
        value: AlgoStrategy.POV,
    },
};

export const ALGO_ORDER_TYPE_OPTIONS: {
    market: TradingFormSelectOption;
    limit: TradingFormSelectOption;
} = {
    market: {
        name: 'orderTypeSelect.options.market',
        value: OrderType.Market,
    },
    limit: {
        name: 'orderTypeSelect.options.limit',
        value: OrderType.Limit,
    },
};

export const BASKET_ORDER_TYPE_OPTIONS: {
    market: TradingFormSelectOption;
    limit: TradingFormSelectOption;
    stop: TradingFormSelectOption;
    stopLimit: TradingFormSelectOption;
} = {
    market: {
        name: 'orderTypeSelect.options.market',
        value: OrderType.Market,
    },
    limit: {
        name: 'orderTypeSelect.options.limit',
        value: OrderType.Limit,
    },
    stop: {
        name: 'orderTypeSelect.options.stop',
        value: OrderType.Stop,
    },
    stopLimit: {
        name: 'orderTypeSelect.options.stopLimit',
        value: OrderType.StopLimit,
    },
};

export const VALIDITY_OPTIONS: {
    day: TradingFormSelectOption;
    gtc: TradingFormSelectOption;
    gtd: TradingFormSelectOption;
} = {
    day: {
        name: 'validitySelect.options.goodForDay',
        value: OrderValidity.GoodForDay,
    },
    gtc: {
        name: 'validitySelect.options.goodTilCanceled',
        value: OrderValidity.GoodTilCanceled,
    },
    gtd: {
        name: 'validitySelect.options.goodTilDate',
        value: OrderValidity.GoodTilDate,
    },
};

export const MARKET_TIME_OPTIONS: {
    preMarket: TradingFormSelectOption;
    coreMarket: TradingFormSelectOption;
    postMarket: TradingFormSelectOption;
    allMarket: TradingFormSelectOption;
    preCoreMarket: TradingFormSelectOption;
    postCoreMarket: TradingFormSelectOption;
} = {
    preMarket: {
        name: 'marketTimeSelect.options.preMarket',
        value: OrderMarketTime.PreMarket,
    },
    coreMarket: {
        name: 'marketTimeSelect.options.coreMarket',
        value: OrderMarketTime.CoreMarket,
    },
    postMarket: {
        name: 'marketTimeSelect.options.postMarket',
        value: OrderMarketTime.PostMarket,
    },
    allMarket: {
        name: 'marketTimeSelect.options.allMarket',
        value: OrderMarketTime.AllMarket,
    },
    preCoreMarket: {
        name: 'marketTimeSelect.options.preCoreMarket',
        value: OrderMarketTime.PreCoreMarket,
    },
    postCoreMarket: {
        name: 'marketTimeSelect.options.postCoreMarket',
        value: OrderMarketTime.PostCoreMarket,
    },
};

export const FILL_TYPE_OPTIONS: {
    any: TradingFormSelectOption;
    aon: TradingFormSelectOption;
} = {
    any: {
        name: 'fillTypeSelect.options.any',
        value: OrderFillType.Any,
    },
    aon: {
        name: 'fillTypeSelect.options.aon',
        value: OrderFillType.AllOrNone,
    },
};

export const POSITION_TYPE_OPTIONS: {
    cash: TradingFormSelectOption;
    margin: TradingFormSelectOption;
} = {
    cash: {
        name: 'positionTypeSelect.options.cash',
        value: PositionType.Cash,
    },
    margin: {
        name: 'positionTypeSelect.options.margin',
        value: PositionType.Margin,
    },
};

export const TRAILING_STOP_PEG_TYPE_OPTIONS: {
    dollar: TradingFormSelectOption;
    percent: TradingFormSelectOption;
} = {
    dollar: {
        name: 'trailingStopPegTypeSelect.options.dollar',
        value: TrailingStopType.Dollar,
    },
    percent: {
        name: 'trailingStopPegTypeSelect.options.percent',
        value: TrailingStopType.Percent,
    },
};

export const OPTION_STRATEGY_OPTIONS: {
    singleLeg: TradingFormSelectOption;
    //TODO: Altering UI to fit buy writes
    // buyWrite: TradingFormSelectOption;
    shortStrangle: TradingFormSelectOption;
    longStrangle: TradingFormSelectOption;
    longCallSpread: TradingFormSelectOption;
    shortCallSpread: TradingFormSelectOption;
    longPutSpread: TradingFormSelectOption;
    shortPutSpread: TradingFormSelectOption;
    longCalendarCallSpread: TradingFormSelectOption;
    longCalendarPutSpread: TradingFormSelectOption;
    shortIronCondor: TradingFormSelectOption;
    longIronCondor: TradingFormSelectOption;
    shortIronButterfly: TradingFormSelectOption;
    longIronButterfly: TradingFormSelectOption;
    syntheticLongStock: TradingFormSelectOption;
    syntheticShortStock: TradingFormSelectOption;
} = {
    singleLeg: {
        name: 'optionStrategySelect.options.singleLeg',
        value: OptionStrategy.SingleLeg,
    },
    // buyWrite: {
    //     name: 'optionStrategySelect.options.buyWrite',
    //     value: OptionStrategy.BuyWrite,
    // },
    shortStrangle: {
        name: 'optionStrategySelect.options.shortStrangle',
        value: OptionStrategy.ShortStrangle,
    },
    longStrangle: {
        name: 'optionStrategySelect.options.longStrangle',
        value: OptionStrategy.LongStrangle,
    },
    longCallSpread: {
        name: 'optionStrategySelect.options.longCallSpread',
        value: OptionStrategy.LongCallSpread,
    },
    shortCallSpread: {
        name: 'optionStrategySelect.options.shortCallSpread',
        value: OptionStrategy.ShortCallSpread,
    },
    longPutSpread: {
        name: 'optionStrategySelect.options.longPutSpread',
        value: OptionStrategy.LongPutSpread,
    },
    shortPutSpread: {
        name: 'optionStrategySelect.options.shortPutSpread',
        value: OptionStrategy.ShortPutSpread,
    },
    longCalendarPutSpread: {
        name: 'optionStrategySelect.options.longCalendarPutSpread',
        value: OptionStrategy.LongCalendarPutSpread,
    },
    longCalendarCallSpread: {
        name: 'optionStrategySelect.options.longCalendarCallSpread',
        value: OptionStrategy.LongCalendarCallSpread,
    },
    shortIronCondor: {
        name: 'optionStrategySelect.options.shortIronCondor',
        value: OptionStrategy.ShortIronCondor,
    },
    longIronCondor: {
        name: 'optionStrategySelect.options.longIronCondor',
        value: OptionStrategy.LongIronCondor,
    },
    shortIronButterfly: {
        name: 'optionStrategySelect.options.shortIronButterfly',
        value: OptionStrategy.ShortIronButterfly,
    },
    longIronButterfly: {
        name: 'optionStrategySelect.options.longIronButterfly',
        value: OptionStrategy.LongIronButterfly,
    },
    syntheticLongStock: {
        name: 'optionStrategySelect.options.syntheticLongStock',
        value: OptionStrategy.SyntheticLongStock,
    },
    syntheticShortStock: {
        name: 'optionStrategySelect.options.syntheticShortStock',
        value: OptionStrategy.SyntheticShortStock,
    },
};

export const PRICE_TYPE_OPTIONS: {
    decimal: TradingFormSelectOption;
    percentage: TradingFormSelectOption;
} = {
    decimal: {
        name: 'priceTypeSelect.options.decimal',
        value: PriceType.Decimal,
    },
    percentage: {
        name: 'priceTypeSelect.options.percentage',
        value: PriceType.Percentage,
    },
};
